import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, XCircle, RefreshCw, X, Film, Link2, Images } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { AuthImage } from "./AuthImage";
import { toast } from "sonner";

const NETWORKS = ["TIKTOK", "INSTAGRAM", "FACEBOOK"]; // ordre de publication
const LABEL = { TIKTOK: "TikTok", INSTAGRAM: "Instagram", FACEBOOK: "Facebook" };

function BrandLogo({ platform }) {
  if (platform === "FACEBOOK")
    return <svg viewBox="0 0 24 24" className="w-6 h-6"><rect width="24" height="24" rx="6" fill="#1877F2" /><path d="M15 8h-1.5c-.5 0-.8.3-.8.8V11H15l-.3 2h-2V19h-2.3v-6h-1.6v-2h1.6V9.3C8.4 7.6 9.4 6.5 11 6.5c.8 0 1.6.1 2 .2V8z" fill="#fff" /></svg>;
  if (platform === "INSTAGRAM")
    return <svg viewBox="0 0 24 24" className="w-6 h-6"><defs><linearGradient id="ig" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor="#FEDA75" /><stop offset=".3" stopColor="#FA7E1E" /><stop offset=".6" stopColor="#D62976" /><stop offset="1" stopColor="#4F5BD5" /></linearGradient></defs><rect width="24" height="24" rx="6" fill="url(#ig)" /><rect x="6" y="6" width="12" height="12" rx="4" fill="none" stroke="#fff" strokeWidth="1.6" /><circle cx="12" cy="12" r="3" fill="none" stroke="#fff" strokeWidth="1.6" /><circle cx="16" cy="8" r="1" fill="#fff" /></svg>;
  return <svg viewBox="0 0 24 24" className="w-6 h-6"><rect width="24" height="24" rx="6" fill="#010101" /><path d="M15.3 6c.3 1.5 1.2 2.6 2.7 2.8v2c-1 .1-1.9-.1-2.8-.6v3.9c0 2.3-1.7 3.9-3.9 3.9-2 0-3.6-1.5-3.6-3.5 0-2.2 1.9-3.7 4.1-3.3v2.1c-.3-.1-.7-.2-1-.1-.8.1-1.3.7-1.2 1.5.1.7.7 1.2 1.4 1.1.8 0 1.3-.6 1.3-1.5V6h2z" fill="#25F4EE" /><path d="M15.7 6c.3 1.5 1.2 2.6 2.7 2.8v2c-1 .1-1.9-.1-2.8-.6v3.9c0 2.3-1.7 3.9-3.9 3.9-2 0-3.6-1.5-3.6-3.5 0-2.2 1.9-3.7 4.1-3.3v2.1c-.3-.1-.7-.2-1-.1-.8.1-1.3.7-1.2 1.5.1.7.7 1.2 1.4 1.1.8 0 1.3-.6 1.3-1.5V6h1.9z" fill="#FE2C55" opacity=".85" /></svg>;
}

export default function SocialPublish() {
  const [conns, setConns] = useState([]);
  const [media, setMedia] = useState(null); // {media_path, kind, source, localUrl?, content_type?}
  const [caption, setCaption] = useState("");
  const [selected, setSelected] = useState({ TIKTOK: true, INSTAGRAM: true, FACEBOOK: true });
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [batch, setBatch] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [csImages, setCsImages] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const loadConns = () => api.get("/social/connections").then((r) => setConns(r.data.connections)).catch(() => {});
  useEffect(() => {
    loadConns();
    api.get("/content/images").then((r) => setCsImages(r.data || [])).catch(() => {});
  }, []);

  const connOf = (p) => conns.find((c) => c.platform === p) || {};

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/social/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia({ media_path: data.media_path, kind: data.kind, source: "upload", content_type: data.content_type, localUrl: URL.createObjectURL(file) });
      toast.success("Média prêt à publier.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'import.");
    } finally {
      setUploading(false);
    }
  };

  const pickCs = (img) => {
    setMedia({ media_path: img.media_path, kind: "photo", source: "content" });
    setBatch(null);
    setShowPicker(false);
  };

  const toggle = (p) => setSelected((s) => ({ ...s, [p]: !s[p] }));

  const publish = async () => {
    const platforms = NETWORKS.filter((p) => selected[p]);
    if (platforms.length === 0) return toast.error("Cochez au moins un réseau pour publier.");
    if (!media) return toast.error("Ajoutez un média (image ou vidéo) à publier.");
    setPublishing(true);
    try {
      const { data } = await api.post("/social/publish", { media_path: media.media_path, caption, platforms });
      setBatch(data);
      if (data.all_success) toast.success("Tout est publié ✅");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publication impossible.");
    } finally {
      setPublishing(false);
    }
  };

  const retry = async (platform) => {
    setRetrying(platform);
    try {
      const { data } = await api.post(`/social/publish/${batch.batch_id}/retry/${platform}`);
      setBatch((b) => ({
        ...b,
        results: b.results.map((r) => (r.platform === platform ? data.result : r)),
        all_success: data.all_success,
      }));
      if (data.result.status === "success") toast.success(`${LABEL[platform]} publié ✅`);
      else toast.error(data.result.error_reason || "Nouvel échec.");
      if (data.all_success) toast.success("Tout est publié ✅");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec du retry.");
    } finally {
      setRetrying(null);
    }
  };

  const connect = async (platform) => {
    if (platform !== "TIKTOK") {
      // Facebook & Instagram : connexion Meta unifiée via l'onglet Connecteurs
      window.location.href = "/connectors";
      return;
    }
    try {
      const { data } = await api.post(`/social/connections/${platform}/connect`);
      if (data.authorization_url) window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Connexion impossible.");
    }
  };

  return (
    <div className="space-y-5" data-testid="social-publish">
      {/* 1. Média */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-200">1. Votre média</h3>
        {media ? (
          <div className="flex items-center gap-4" data-testid="selected-media">
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-zinc-900 shrink-0 flex items-center justify-center">
              {media.source === "upload" && media.content_type?.startsWith("video/") ? (
                <video src={media.localUrl} className="w-full h-full object-cover" muted />
              ) : media.source === "upload" ? (
                <img src={media.localUrl} alt="aperçu" className="w-full h-full object-cover" />
              ) : (
                <AuthImage path={media.media_path} alt="aperçu" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-zinc-300 flex items-center gap-2">
                {media.kind === "video" ? <Film className="w-4 h-4" /> : <Images className="w-4 h-4" />}
                {media.kind === "video" ? "Vidéo" : "Image"} prête
              </p>
              <button onClick={() => { setMedia(null); setBatch(null); }} data-testid="clear-media"
                className="text-xs text-zinc-500 hover:text-red-400 mt-1 inline-flex items-center gap-1"><X className="w-3 h-3" /> Retirer</button>
            </div>
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); }}
            className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${drag ? "border-amber-500 bg-amber-500/5" : "border-zinc-700"}`}
            data-testid="upload-dropzone">
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" />
            ) : (
              <>
                <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Glissez-déposez une image ou une vidéo</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <button onClick={() => fileRef.current?.click()} data-testid="upload-btn"
                    className="px-3 h-9 rounded-xl gold-gradient text-black text-sm font-semibold">Choisir un fichier</button>
                  {csImages.length > 0 && (
                    <button onClick={() => setShowPicker((v) => !v)} data-testid="pick-content-btn"
                      className="px-3 h-9 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-zinc-300">Depuis Content Studio</button>
                  )}
                </div>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])} data-testid="file-input" />
          </div>
        )}
        {showPicker && !media && (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 pt-2" data-testid="content-picker">
            {csImages.map((img) => (
              <button key={img.id} onClick={() => pickCs(img)} data-testid={`pick-${img.id}`}
                className="rounded-xl overflow-hidden border border-zinc-800 hover:border-amber-500 aspect-square">
                <AuthImage path={img.media_path} alt={img.prompt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 2. Légende */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-2">
        <h3 className="text-sm font-semibold text-zinc-200">2. Légende</h3>
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} maxLength={2200}
          data-testid="caption-input" placeholder="Une seule légende, appliquée à tous les réseaux sélectionnés."
          className="w-full bg-[#0F0F13] border border-zinc-800 rounded-2xl p-3 text-sm text-zinc-200 focus:outline-none focus:border-amber-500 resize-none" />
      </div>

      {/* 3. Réseaux */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-200">3. Réseaux</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {NETWORKS.map((p) => {
            const c = connOf(p);
            const badge = !c.configured ? ["Mode simulation", "text-amber-300 bg-amber-500/10"]
              : c.connected ? ["Connecté", "text-emerald-300 bg-emerald-500/10"]
              : ["À connecter", "text-zinc-300 bg-zinc-700/30"];
            return (
              <div key={p} onClick={() => toggle(p)} data-testid={`network-${p}`}
                className={`cursor-pointer rounded-2xl p-3 border transition-colors ${selected[p] ? "border-amber-500 bg-amber-500/5" : "border-zinc-800 hover:border-zinc-700"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><BrandLogo platform={p} /><span className="text-sm font-medium text-zinc-200">{LABEL[p]}</span></div>
                  <span data-testid={`network-check-${p}`}
                    className={`w-5 h-5 rounded-md border flex items-center justify-center ${selected[p] ? "bg-amber-500 border-amber-500" : "border-zinc-600"}`}>
                    {selected[p] && <CheckCircle2 className="w-4 h-4 text-black" />}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${badge[1]}`}>{badge[0]}</span>
                  {c.configured && !c.connected && (
                    <button onClick={(e) => { e.stopPropagation(); connect(p); }} data-testid={`connect-${p}`}
                      className="text-[11px] text-amber-400 hover:underline inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Connecter</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={publish} disabled={publishing} data-testid="publish-btn"
          className="w-full h-12 rounded-2xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {publishing ? <><Loader2 className="w-5 h-5 animate-spin" /> Publication…</> : "Publier"}
        </button>
      </div>

      {/* 4. Résultats */}
      {batch && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-3" data-testid="publish-results">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-200">Résultat de la publication</h3>
            {batch.all_success && <span className="text-sm text-emerald-400 font-semibold" data-testid="all-success">Tout est publié ✅</span>}
          </div>
          {batch.results.map((r) => (
            <div key={r.platform} data-testid={`result-${r.platform}`}
              className="flex items-start gap-3 bg-[#0F0F13] border border-zinc-800 rounded-2xl p-3">
              <BrandLogo platform={r.platform} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-200 flex items-center gap-2">
                  {r.status === "success" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                  {r.label} — {r.status === "success" ? (r.note || "Publié") : "Non publié"}
                  {r.simulated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300">SIMULATION</span>}
                </p>
                {r.status === "failed" && <p className="text-xs text-zinc-400 mt-1">{r.error_reason}</p>}
                {r.can_retry && (
                  <button onClick={() => retry(r.platform)} disabled={retrying === r.platform}
                    data-testid={`retry-${r.platform}`}
                    className="mt-2 px-3 h-8 rounded-xl bg-zinc-900 border border-amber-500/40 text-amber-300 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-60">
                    {retrying === r.platform ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Réessayer {r.label} uniquement
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
